import dayjs from "dayjs"
import { useEffect } from "react"
import * as Yup from "yup"

import useDialogConfirmClose from "@hooks/useDialogConfirmClose"
import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"

import { Dialog, Prompt } from "@molecules"
import DialogFooter from "@molecules/Dialog/DialogFooter/DialogFooter"
import DialogHeader from "@molecules/Dialog/DialogHeader/DialogHeader"
import { PasswordInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import toast from "@molecules/Toast/Toast"

import { PASSWORD_ENDPOINTS } from "@endpoints/password"

import styles from "./SetPasswordDialog.module.scss"
import {
    PasswordChangeErrorResponse,
    PasswordChangePayload,
    PasswordConfirmationForm,
    SetPasswordDialogProps,
} from "./SetPasswordDialog.types"

const formSchema: Yup.ObjectSchema<PasswordConfirmationForm> = Yup.object().shape({
    new_password: Yup.string()
        .required("Please enter a password")
        .min(8, "Password length should be at least 8 characters")
        .matches(/[a-zA-Z]/, "Password must contain at least 1 letter")
        .matches(/[\d]/, "Password must contain at least 1 number"),
    new_password_confirm: Yup.string()
        .required("Please confirm your password")
        .min(8, "Password length should be at least 8 characters")
        .matches(/[a-zA-Z]/, "Password must contain at least 1 letter")
        .matches(/[\d]/, "Password must contain at least 1 number")
        .oneOf([Yup.ref("new_password")], "Passwords do not match"),
})

export default function SetPasswordDialog(props: SetPasswordDialogProps) {
    const { children, isDefaultOpen, onOpenChange = () => {} } = props

    const { user, updateUser } = useUser()
    const isDesktop = useIsDesktop()

    const {
        isDialogOpen,
        isPromptOpen,
        closeDialog,
        handleCloseAttempt,
        handleDialogOpenChange,
        handlePromptCancel,
        handlePromptDiscard,
    } = useDialogConfirmClose({
        defaultOpen: isDefaultOpen,
        onOpenChange,
    })

    const payloadFormatter = (data: PasswordConfirmationForm) => {
        return {
            new_password: data.new_password,
            new_password_confirm: data.new_password_confirm,
        }
    }

    const setOrUpdatePasswordCopy = user?.hasPassword ? "Update password" : "Set password"

    const onMutationSuccess = () => {
        closeDialog()

        updateUser({
            // todo: once we have user timezone setup, update this
            password_last_modified_at: dayjs().toDate(),
        })
    }

    const onMutationError = () => {
        closeDialog()
    }

    return (
        <>
            <Dialog.Root mode="dialog" variant="default" isOpen={isDialogOpen} onOpenChange={handleDialogOpenChange}>
                {children && <Dialog.Trigger>{children}</Dialog.Trigger>}
                <Dialog.Content className={styles.dialogContent}>
                    <DialogHeader.Root>
                        <DialogHeader.Title title={setOrUpdatePasswordCopy} />
                        {isDesktop && <DialogHeader.CloseButton onClick={handleCloseAttempt} />}
                    </DialogHeader.Root>
                    <GenericForm<PasswordConfirmationForm, void, PasswordChangeErrorResponse, PasswordChangePayload>
                        formConfig={{
                            defaultValues: {
                                new_password: "",
                                new_password_confirm: "",
                            },
                        }}
                        formValidationSchema={formSchema}
                        mutationConfig={{
                            endpoint: PASSWORD_ENDPOINTS.CHANGE_PASSWORD,
                            method: "POST",
                            genericErrorMessage: "",
                            genericSuccessMessage: user?.hasPassword ? "Password updated" : "Password set",
                            payloadFormatter,
                        }}
                        onMutationSuccess={onMutationSuccess}
                        onMutationError={onMutationError}
                        triggerFormReset={!isDialogOpen && !isPromptOpen}
                    >
                        {({ isPending, nonFieldError, form }) => {
                            const passwords = form.getValues()
                            const formIsValid = form.formState.isValid
                            const passwordsMatch = passwords.new_password === passwords.new_password_confirm

                            // Handle non-field errors that should show as toasts
                            useEffect(() => {
                                if (nonFieldError) {
                                    closeDialog()

                                    // nonFieldError is an array from backend's __all__ field
                                    const errorMessage = Array.isArray(nonFieldError)
                                        ? nonFieldError[0]
                                        : nonFieldError

                                    if (errorMessage.includes("Account verification has expired")) {
                                        toast({
                                            type: "error",
                                            variant: "detailed",
                                            title: "Account verification has expired",
                                            supportText: "Please try again.",
                                        })
                                    } else {
                                        toast({
                                            type: "error",
                                            variant: "detailed",
                                            title: "Could not set password",
                                            supportText: "Something went wrong. Please try again.",
                                        })
                                    }
                                }
                            }, [nonFieldError])

                            return (
                                <>
                                    <Dialog.Body>
                                        <div className={styles.passwordInputs}>
                                            <PasswordInput<PasswordConfirmationForm>
                                                size="md"
                                                autoFocus={isDialogOpen}
                                                displayStrengthIndicator={true}
                                                label={user?.hasPassword ? "New password" : "Password"}
                                                placeholder={
                                                    user?.hasPassword ? "Enter new password..." : "Enter password..."
                                                }
                                                name="new_password"
                                            />
                                            <PasswordInput<PasswordConfirmationForm>
                                                size="md"
                                                label={user?.hasPassword ? "Confirm new password" : "Confirm password"}
                                                placeholder={
                                                    user?.hasPassword
                                                        ? "Enter new password again..."
                                                        : "Enter password again..."
                                                }
                                                displayValidationMessages={false}
                                                inputHelpText={
                                                    passwordsMatch && formIsValid
                                                        ? {
                                                              status: "success",
                                                              message: "Passwords match",
                                                          }
                                                        : {
                                                              status: "neutral",
                                                              neutralIcon: faIcon({
                                                                  name: "circle-check",
                                                                  style: "regular",
                                                              }),
                                                              message: "Passwords must match",
                                                          }
                                                }
                                                name="new_password_confirm"
                                            />
                                        </div>
                                    </Dialog.Body>
                                    <DialogFooter.Root>
                                        <DialogFooter.LeftSide>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="lg"
                                                isProcessing={isPending}
                                                onClick={handleCloseAttempt}
                                                noDebounce={true}
                                            >
                                                Cancel
                                            </Button>
                                        </DialogFooter.LeftSide>
                                        <DialogFooter.RightSide>
                                            <Button type="submit" variant="solid" size="lg" isProcessing={isPending}>
                                                {setOrUpdatePasswordCopy}
                                            </Button>
                                        </DialogFooter.RightSide>
                                    </DialogFooter.Root>
                                </>
                            )
                        }}
                    </GenericForm>
                </Dialog.Content>
            </Dialog.Root>

            <Prompt
                isOpen={isPromptOpen}
                title="Discard progress?"
                body="If you discard, your progress will not be saved."
                onCancel={handlePromptCancel}
                onConfirm={handlePromptDiscard}
                cancelText="Cancel"
                confirmText="Discard"
                isDangerousOperation={true}
            />
        </>
    )
}
